/**
 * Shared legacy recoverability flags and the document upload hold policy.
 *
 * Used in two places:
 *   1. Every saga reducer's *_SAVE_FAILURE / *_RESTORE_FAILURE branch
 *      (generated from `server/src/generators/redux.ts`). The reducer sets
 *      `entity.fatalError.recoverable` based on the result of
 *      `isRecoverableError(status, message)`. The flag is consumed by
 *      Support → Cache Management UI, which hides recoverable errors from
 *      the operator (the queue will retry them) and surfaces only the
 *      unrecoverable ones for human attention.
 * The document queue uses `isHeldDocumentStatus` instead of the legacy
 * `recoverable` flag. Storage 403 and server 5xx failures get bounded retry;
 * invalid document 4xx parameters wait for correction.
 *
 * Previously this constant + logic was duplicated in 56 generated
 * reducer files AND a dead copy in `processQueue.ts`. Drift between any
 * pair would silently change the Cache Management UI's behavior. This
 * module collapses them all into one definition.
 *
 * @module client/lib/recoverableErrors
 */

/**
 * PostgreSQL SQLSTATE prefixes (5-character class codes) that we treat as
 * transient server-side conditions worth retrying. The matcher uses
 * `startsWith` against the error message — server error messages typically
 * lead with the SQLSTATE code. Codes are documented at:
 *   https://www.postgresql.org/docs/current/errcodes-appendix.html
 */
export const OK_DATABASE_CODES: readonly string[] = [
    '53000', // Insufficient resources
    '53100', // Disk full
    '53200', // Out of memory
    '53300', // Too many connections
    '53400', // Configuration limit exceeded
    '58000', // system_error
    'XX000', // Internal error, usually control plane request failed
    'XX100', // Data corrupted
    'XX200', // Index corrupted
];

/**
 * HTTP statuses we always treat as transient regardless of message content
 * (502 Bad Gateway, 503 Service Unavailable, 504 Gateway Timeout).
 */
export const OK_HTTP_CODES: readonly number[] = [502, 503, 504];

/**
 * HTTP 4xx statuses we treat as application-level rejections that won't be
 * fixed by a simple retry of the same payload (bad request, forbidden, not
 * found, conflict, validation failure, …). Note 408 (request timeout) is
 * intentionally absent — that one IS retryable.
 */
export const UNRECOVERABLE_HTTP_CODES: readonly number[] = [
    400, 403, 404, 405, 406, 409, 411, 413, 414, 415, 416, 422,
];

/** Document 4xx responses that require changing the queued operation. */
export function isHeldDocumentStatus(status: number): boolean {
    return status >= 400 && status < 500 && ![401, 403, 408, 429].includes(status);
}

/**
 * Returns true iff the error message starts with one of the recoverable
 * PostgreSQL SQLSTATE codes.
 */
export function hasRecoverableDatabaseCode(message: string | null | undefined): boolean {
    if (!message) return false;
    return OK_DATABASE_CODES.some(code => message.startsWith(code));
}

/**
 * Decide whether a saga's `*_SAVE_FAILURE` action represents a transient
 * condition (worth retrying) or a permanent rejection (worth surfacing
 * to the user). Mirror of the historical inline logic in every generated
 * reducer.
 *
 * Returns `true` when the failure looks transient. The reducer should set
 * `entity.fatalError.recoverable = isRecoverableError(status, message)`.
 *
 * Recoverability rules (in priority order):
 *   - If the message starts with a known transient PG code → recoverable.
 *   - If the HTTP status is one of [502, 503, 504] → recoverable.
 *   - If the HTTP status is ≥500 → unrecoverable (server-side internal
 *     error not in the gateway-timeout family).
 *   - If the HTTP status is in the unrecoverable 4xx list → unrecoverable.
 *   - Anything else → recoverable (default-to-transient, conservative).
 */
export function isRecoverableError(status: number, message: string | null | undefined): boolean {
    if (hasRecoverableDatabaseCode(message)) return true;
    if (OK_HTTP_CODES.includes(status)) return true;
    if (status >= 500) return false;
    if (UNRECOVERABLE_HTTP_CODES.includes(status)) return false;
    return true;
}
